/**
 * Memory Types Badges Component
 *
 * Displays memory type capabilities for integrations using data from
 * integrations/capabilities/memoryModalities.ts.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { FileText, Mic, Image as ImageIcon, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from './badge';
import { memoryTypes, getIntegrationMemoryCapabilities } from '@/lib/memoryTypes';
import type { ModalityId } from '@/integrations/capabilities/memoryModalities';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

interface MemoryTypesBadgesProps {
  integrationId: string;
  compact?: boolean;
  className?: string;
  onPlannedClick?: (memoryTypeId: string) => void;
}

const modalityIcons: Record<string, LucideIcon> = {
  text: FileText,
  audio: Mic,
  image: ImageIcon,
  video: Video,
};

interface MemoryTypeBadgeProps {
  memoryTypeId: string;
  capability: 'contribute' | 'access' | 'limited' | 'unavailable' | 'blocked';
  isPlanned?: boolean;
  limitation?: string;
  compact?: boolean;
  onClick?: () => void;
}

const MemoryTypeBadge: React.FC<MemoryTypeBadgeProps> = ({
  memoryTypeId,
  capability,
  isPlanned = false,
  limitation,
  compact = false,
  onClick,
}) => {
  const { t } = useTranslation('integrations');
  const memoryType = memoryTypes[memoryTypeId];
  if (!memoryType) return null;

  const Icon = modalityIcons[memoryTypeId] ?? FileText;

  const typeName = t(`memoryModalities.types.${memoryTypeId}`, {
    defaultValue: memoryType.name,
  });

  if (memoryTypeId === 'image' || memoryTypeId === 'video') {
    return (
      <Badge
        variant="outline"
        className="text-xs font-medium border bg-orange-50 text-orange-700 border-orange-200"
      >
        <Link
          to="/roadmap/image-memories#image-interest"
          className="inline-flex items-center gap-1.5"
        >
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {memoryTypeId === 'video'
              ? t('memoryModalities.videoEarlyAccess')
              : t('memoryModalities.imagesEarlyAccess')}
          </span>
        </Link>
      </Badge>
    );
  }

  const stateLabel = () => {
    if (isPlanned) {
      return t('memoryModalities.badge.planned');
    }
    if (capability === 'blocked') {
      return t('memoryModalities.badge.blocked');
    }
    if (capability === 'contribute') {
      return t('memoryModalities.badge.canAdd');
    }
    if (capability === 'access') {
      return t('memoryModalities.badge.canRead');
    }
    if (capability === 'limited') {
      return t('memoryModalities.badge.limited');
    }
    return t('memoryModalities.badge.notSupported');
  };

  const getBadgeVariant = () => {
    if (capability === 'blocked') return 'destructive';
    if (capability === 'contribute') return 'default';
    if (capability === 'access') return 'secondary';
    if (capability === 'limited') return 'outline';
    return 'destructive';
  };

  const getBadgeColor = () => {
    if (isPlanned) {
      return memoryType.color === 'orange'
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : memoryType.color === 'purple'
          ? 'bg-purple-50 text-purple-700 border-purple-200'
          : 'bg-gray-50 text-gray-600 border-gray-200';
    }

    if (capability === 'blocked') return 'bg-red-50 text-red-800 border-red-200';
    if (capability === 'contribute') return 'bg-green-50 text-green-700 border-green-200';
    if (capability === 'access') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (capability === 'limited') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-gray-50 text-gray-500 border-gray-200';
  };

  const labelContent = compact ? (
    <span className="inline-flex items-center gap-1">
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{typeName}</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{typeName}</span>
      <span className="opacity-70" aria-hidden>
        ·
      </span>
      <span>{stateLabel()}</span>
    </span>
  );

  const badge = (
    <Badge
      variant={getBadgeVariant()}
      className={`text-xs font-medium border ${getBadgeColor()} ${
        isPlanned || capability === 'limited' || capability === 'blocked'
          ? 'cursor-pointer hover:opacity-80'
          : ''
      }`}
      onClick={onClick}
    >
      {compact ? (
        <span className="inline-flex items-center gap-1" title={stateLabel()}>
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="sr-only">
            {typeName}: {stateLabel()}
          </span>
          <span aria-hidden>{typeName}</span>
        </span>
      ) : (
        labelContent
      )}
    </Badge>
  );

  const tooltipBody =
    limitation ||
    (isPlanned && memoryTypeId === 'video' ? t('memoryModalities.videoPlannedTooltip') : undefined);

  if (tooltipBody) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">{tooltipBody}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
};

export const MemoryTypesBadges: React.FC<MemoryTypesBadgesProps> = ({
  integrationId,
  compact = false,
  className = '',
  onPlannedClick,
}) => {
  const capabilities = getIntegrationMemoryCapabilities(integrationId);
  const allMemoryTypeIds = Object.keys(memoryTypes);

  const getMemoryTypeBadges = () => {
    return allMemoryTypeIds.map((memoryTypeId) => {
      const memoryType = memoryTypes[memoryTypeId];
      const canContribute = capabilities.canContribute.includes(memoryTypeId);
      const canAccess = capabilities.canAccess.includes(memoryTypeId);
      const limitation = capabilities.limitations?.[memoryTypeId];
      const level = capabilities.levels?.[memoryTypeId as ModalityId];

      const isVideoGlobalPlanned =
        memoryTypeId === 'video' &&
        memoryType.status === 'planned' &&
        !canContribute &&
        !canAccess;

      let capability: 'contribute' | 'access' | 'limited' | 'unavailable' | 'blocked';
      let isPlanned = false;

      if (level === 'blocked') {
        capability = 'blocked';
      } else if (isVideoGlobalPlanned) {
        capability = 'unavailable';
        isPlanned = true;
      } else if (level === 'planned') {
        capability = 'unavailable';
        isPlanned = true;
      } else if (canContribute && canAccess) {
        capability = 'contribute';
      } else if (canAccess) {
        capability = 'access';
      } else if (level === 'limited' || limitation) {
        capability = 'limited';
      } else {
        capability = 'unavailable';
      }

      return (
        <MemoryTypeBadge
          key={memoryTypeId}
          memoryTypeId={memoryTypeId}
          capability={capability}
          isPlanned={isPlanned}
          limitation={limitation}
          compact={compact}
          onClick={isPlanned && onPlannedClick ? () => onPlannedClick(memoryTypeId) : undefined}
        />
      );
    });
  };

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {getMemoryTypeBadges()}
    </div>
  );
};

export default MemoryTypesBadges;
